import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@/context/NotificationContext";
import { fetchEventNotification } from "../api/fetchEventNotification/fetchEventNotificationApi";
import { updateEventNotification } from "../api/updateEventNotification/updateEventNotificationApi";
import { EventNotification } from "../api/fetchEventNotifications/types";

const EventNotificationFormSchema = z.object({
  isMailChannelEnabled: z.boolean(),
  emailTemplateId: z.number().nullable(),
  emailSubject: z.string(),
  emailContent: z.string(),
  isSmsChannelEnabled: z.boolean(),
  smsTemplateId: z.number().nullable(),
  smsContent: z.string(),
});

export type EventNotificationFormData = z.infer<
  typeof EventNotificationFormSchema
>;

export function useEditEventNotification() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<EventNotification | null>(
    null,
  );

  const defaultValues: EventNotificationFormData = {
    isMailChannelEnabled: false,
    emailTemplateId: null,
    emailSubject: "",
    emailContent: "",
    isSmsChannelEnabled: false,
    smsTemplateId: null,
    smsContent: "",
  };

  const form = useForm<EventNotificationFormData>({
    resolver: zodResolver(EventNotificationFormSchema),
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (notification) {
      form.reset({
        isMailChannelEnabled: notification.isMailChannelEnabled,
        emailTemplateId: notification.emailTemplateId,
        emailSubject: notification.emailTemplateSubject || "",
        emailContent: notification.emailTemplateContent || "",
        isSmsChannelEnabled: notification.isSmsChannelEnabled,
        smsTemplateId: notification.smsTemplateId,
        smsContent: notification.smsTemplateContent || "",
      });
    }
  }, [notification, form]);

  const fetchNotification = useCallback(
    async (id: number) => {
      setIsLoading(true);
      try {
        const response = await fetchEventNotification(id);
        setNotification(response.data);
      } catch (error) {
        console.error("Error fetching notification:", error);
        showNotification("Error", "Failed to fetch notification", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification],
  );

  const submitForm = useCallback(
    async (values: EventNotificationFormData) => {
      if (!notification) {
        showNotification("Error", "No notification loaded to update", "error");
        return;
      }

      setIsLoading(true);
      try {
        await updateEventNotification(notification.id, {
          isMailChannelEnabled: values.isMailChannelEnabled,
          isSmsChannelEnabled: values.isSmsChannelEnabled,
          emailSubject: values.emailSubject || null,
          emailContent: values.emailContent || null,
          smsContent: values.smsContent || null,
        });
        showNotification(
          "Success!",
          "Notification has been successfully updated!",
          "success",
        );
        navigate("/admin/event-notifications");
      } catch (error) {
        console.error("Error updating notification:", error);
        if (error instanceof Error) {
          showNotification(
            "Error",
            error.message || "Failed to update notification",
            "error",
          );
        } else {
          showNotification(
            "Error",
            "An unknown error occurred while updating the notification.",
            "error",
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, showNotification, notification],
  );

  const handleCancel = useCallback(() => {
    navigate("/admin/event-notifications");
  }, [navigate]);

  return {
    form,
    submitForm,
    fetchNotification,
    handleCancel,
    notification,
    isLoading,
  };
}
